<script setup lang="ts">
import { moveArrayElement, useSortable } from '@vueuse/integrations/useSortable'
import { useFileDialog } from '@vueuse/core'

interface ImagePreview {
  type: string
  src: string
}
interface ImagePreviewWithId extends ImagePreview {
  id: number
}
const props = defineProps<{ button?: string, multiple?: boolean, recordId?: string, collectionId?: string, images?: string[] | string | File[] | File, accept?: string }>()
const emit = defineEmits(['updated'])
const toast = useToast()
const loading = ref(false)

const { open, onChange } = useFileDialog({
  accept: props.accept ?? 'image/*', // Set to accept only image files
  multiple: props.multiple,
})

const propFiles = computed(() => props.images)
const maxFileSize = ref(5) // MB
watch(propFiles, async (val) => {
  await getImagePreviews(propFiles.value)
})

async function displayPreview(item: string | File, index: number = 0): Promise<ImagePreviewWithId> {
  let res: ImagePreviewWithId = { id: index, type: '', src: '' }
  if (item instanceof File) {
    const d = await readUploadedFileAsImage(item)
    res = { ...res, ...d }
  }
  else {
    res = { ...res, type: 'string', src: getImagePath({ collectionId: props.collectionId, id: props.recordId, src: item }) }
  }
  return res
}

const el = ref<HTMLElement | null>(null)
const imagePreviews = ref<ImagePreviewWithId[]>([])

useSortable(el, imagePreviews, {
  disabled: !props.multiple,
  animation: 200,
  onEnd: (e) => {
    if (Array.isArray(propFiles.value)) {
      const files = [...propFiles.value]
      moveArrayElement(files, e.oldIndex, e.newIndex)
      emit('updated', files)
    }
  },
})

async function getImagePreviews(_images: string[] | string | File[] | File | null = null) {
  let images = []
  if (!_images)
    return []
  if (Array.isArray(_images)) {
    for (let i = 0; i < _images.length; i++) {
      const image = await displayPreview(_images[i], i)
      images.push(image)
    }
  }
  else {
    const image = await displayPreview(_images)
    images = [image]
  }
  imagePreviews.value = images
}

onMounted(async () => {
  await getImagePreviews(propFiles.value)
})

onChange(async (afiles) => {
  if (!afiles || !afiles.length)
    return
  // ✅ Filter out files larger than 5MB (5 * 1024 * 1024 bytes)
  const maxSize = maxFileSize.value * 1024 * 1024
  const validFiles = Array.from(afiles).filter(file => file.size <= maxSize)

  if (validFiles.length < afiles.length) {
    toast.add({
      title: 'Some files were too large',
      description: `Files must be smaller than ${maxFileSize.value}MB`,
      color: 'red',
      icon: 'ri:error-warning-line',
    })
  }

  if (validFiles.length === 0) {
    loading.value = false
    return
  }
  loading.value = true
  if (afiles?.length) {
    if (props.multiple) {
      emit('updated', [...propFiles.value, ...afiles])
      await getImagePreviews([...propFiles.value, ...afiles])
    }
    else {
      emit('updated', afiles[0])
      await getImagePreviews(afiles[0])
    }
  }

  loading.value = false
})

function removeImage(index: number) {
  if (Array.isArray(propFiles.value) && propFiles.value.length) {
    const files = [...propFiles.value]
    files.splice(index, 1)
    emit('updated', files)
  }
}

function isDisplayableImage(src: string): boolean {
  if (!src) return false
  if (src?.startsWith('data:')) {
    const mimeMatch = src.match(/^data:([^;]+);/)
    if (!mimeMatch) return false

    const mimeType = mimeMatch[1].toLowerCase()
    return mimeType.startsWith('image/')
  }

  // For non-data URIs, you could check the file extension if needed:
  try {
    const url = new URL(src)
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url.pathname)
  }
  catch {
    return false // Not a valid URL
  }
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-6">
    <UButton
      v-if="!button"
      icon="ri:image-circle-fill"
      :loading="loading"
      class="mx-auto"
      type="button"
      @click="open"
    >
      {{ (!multiple && images) ? 'Change'
        : 'Upload' }} {{ multiple ? 'Images' : 'Image' }}
      <span class="text-xs opacity-75">(Max Image size {{ maxFileSize }}MB)</span>
    </UButton>
    <UButton
      v-else
      icon="ri:image-circle-fill"
      :loading="loading"
      class="mx-auto"
      type="button"
      @click="open"
    >
      {{ button }}
    </UButton>
    <div
      ref="el"
      class="flex flex-wrap  w-full lg:flex-1 justify-center"
    >
      <div
        v-for="(image, index) in imagePreviews"
        :key="image.id"
        class="w-1/2  px-2 py-2  md:w-32 md:h-28 lg:w-40 lg:h-32 max-h-40 relative"
        :class="[multiple ? 'cursor-grab' : ' ']"
      >
        <UButton
          v-if="multiple"
          icon="heroicons:trash-solid"
          color="red"
          class="p-1 absolute top-0 right-0 text-white inline-flex justify-center items-center"
          aria-label="Remove"
          size="xs"
          @click="removeImage(index)"
        />
        <DownloadFull
          v-if="!isDisplayableImage(image.src)"
          :src="image.src"
          class="w-full h-full"
        />
        <img
          v-else-if="image?.type === 'file'"
          :src="image.src"
          class="rounded shadow  w-full h-full object-contain bg-slate-500/10 "
        >
        <NuxtImg
          v-else-if="recordId && image.src"
          :src="image.src"
          class="rounded shadow w-full h-full object-contain bg-slate-500/10 "
        />
        <slot
          name="image"
          :image="image"
        />
      </div>
    </div>
  </div>
</template>
